import type { RegistrationDraft } from './auth.types';
import type { AuthPath } from './auth.paths.model';
import { LOGIN_PATH, REGISTRATION_PATH, authPath } from './auth.paths.model';

export const REGISTRATION_FLOW_SOURCES = ['signup', 'signin'] as const;
export type RegistrationFlowSource = (typeof REGISTRATION_FLOW_SOURCES)[number];

export type RegisterContinueEvent =
  | 'Email Submitted'
  | 'Full Name Submitted'
  | 'Instagram Submitted';

export type RegisterStepProgress = {
  currentStep: number;
  totalSteps: number;
  showBack: boolean;
};

export type RegisterPersistedField = keyof RegistrationDraft;
export type RegisterField = RegisterPersistedField | 'code';
export type RegisterProgressRequirement =
  | 'valid-email'
  | 'required-full-name'
  | 'required-instagram';
export type RegisterContinueRequirement =
  | RegisterProgressRequirement
  | 'optional-valid-phone'
  | 'exact-confirmation-code';

type RegisterStepDefinition = {
  fields: readonly RegisterField[];
  persistedFields: readonly RegisterPersistedField[];
  requirement: RegisterContinueRequirement;
  participatesInProgress?: boolean;
  continueEvent?: RegisterContinueEvent;
};

const toKeys = <TValue extends Record<string, unknown>>(
  value: TValue,
): Array<keyof TValue & string> => {
  return Object.keys(value) as Array<keyof TValue & string>;
};

export const registerStepConfig = {
  email: {
    fields: ['email'],
    persistedFields: ['email'],
    requirement: 'valid-email',
    participatesInProgress: true,
    continueEvent: 'Email Submitted',
  },
  name: {
    fields: ['firstName', 'lastName'],
    persistedFields: ['firstName', 'lastName'],
    requirement: 'required-full-name',
    participatesInProgress: true,
    continueEvent: 'Full Name Submitted',
  },
  instagram: {
    fields: ['firstName', 'instagram'],
    persistedFields: ['instagram'],
    requirement: 'required-instagram',
    participatesInProgress: true,
    continueEvent: 'Instagram Submitted',
  },
  phone: {
    fields: ['phoneCountryDialCode', 'phoneNumber'],
    persistedFields: ['phoneCountryDialCode', 'phoneNumber'],
    requirement: 'optional-valid-phone',
  },
  code: {
    fields: ['email', 'code'],
    persistedFields: [],
    requirement: 'exact-confirmation-code',
  },
} as const satisfies Record<string, RegisterStepDefinition>;

export type RegisterStep = keyof typeof registerStepConfig;
export const REGISTER_FLOW_STEPS = toKeys(registerStepConfig);

type SignupStepConfig = {
  step: RegisterStep;
  path: AuthPath;
};

type LoginStepConfig = {
  step: RegisterStep;
  path: AuthPath;
};

type ByStepId<TSteps extends readonly { step: PropertyKey }[]> = {
  [TStep in TSteps[number]['step']]: Extract<TSteps[number], { step: TStep }>;
};

const toStepMap = <TSteps extends readonly { step: PropertyKey }[]>(
  steps: TSteps,
): ByStepId<TSteps> => {
  return Object.fromEntries(
    steps.map((stepConfig) => [stepConfig.step, stepConfig]),
  ) as ByStepId<TSteps>;
};

export const registrationSteps = [
  {
    step: 'email',
    path: authPath('/signup'),
  },
  {
    step: 'name',
    path: `${REGISTRATION_PATH}/fill-name`,
  },
  {
    step: 'instagram',
    path: `${REGISTRATION_PATH}/instagram`,
  },
  {
    step: 'phone',
    path: `${REGISTRATION_PATH}/phone`,
  },
  {
    step: 'code',
    path: `${REGISTRATION_PATH}/confirmation-code`,
  },
] as const satisfies readonly SignupStepConfig[];

export const loginSteps = [
  {
    step: 'email',
    path: LOGIN_PATH,
  },
  {
    step: 'code',
    path: authPath('/login/confirmation-code'),
  },
] as const satisfies readonly LoginStepConfig[];

export type LoginStep = (typeof loginSteps)[number]['step'];
export const LOGIN_FLOW_STEPS = loginSteps.map((stepConfig) => stepConfig.step);

type SignupFirstStep = (typeof registrationSteps)[0]['step'];
export type ProfileFlowStep = Exclude<RegisterStep, SignupFirstStep>;
export const PROFILE_FLOW_STEPS = registrationSteps
  .slice(1)
  .map((stepConfig) => stepConfig.step) as Array<ProfileFlowStep>;

const signupStepConfig = toStepMap(registrationSteps);
const loginStepConfig = toStepMap(loginSteps);

const getRegisterStepDefinition = (
  step: RegisterStep,
): RegisterStepDefinition => {
  return registerStepConfig[step];
};

const getSignupFlowStepConfig = (step: RegisterStep): SignupStepConfig => {
  return signupStepConfig[step];
};

const registerStepSet = new Set<string>(REGISTER_FLOW_STEPS);
const loginStepSet = new Set<string>(LOGIN_FLOW_STEPS);
const profileFlowStepSet = new Set<string>(PROFILE_FLOW_STEPS);
const persistedFieldSet = new Set<string>(
  Object.values(registerStepConfig).flatMap((stepConfig) =>
    stepConfig.persistedFields.map((field) => field),
  ),
);

const toRegisterFlowPaths = (): Array<AuthPath> => {
  const signupPaths = registrationSteps.map((stepConfig) => stepConfig.path);
  return [...signupPaths, loginStepConfig.code.path];
};

export const REGISTER_FLOW_PATHS = toRegisterFlowPaths();
export type RegisterFlowPath = (typeof REGISTER_FLOW_PATHS)[number];

const registerFlowPathSet = new Set<string>(REGISTER_FLOW_PATHS);

export const isRegisterStep = (value: unknown): value is RegisterStep => {
  return typeof value === 'string' && registerStepSet.has(value);
};

const isLoginStep = (step: RegisterStep): step is LoginStep => {
  return loginStepSet.has(step);
};

const isRegisterFlowPath = (pathname: string): pathname is RegisterFlowPath => {
  return registerFlowPathSet.has(pathname);
};

export const toRegisterFlowPath = (
  pathname: string,
): RegisterFlowPath | null => {
  if (isRegisterFlowPath(pathname)) {
    return pathname;
  }

  return null;
};

export const getSignupStepForPath = (
  pathname: RegisterFlowPath,
): RegisterStep | null => {
  const matchedStep = registrationSteps.find(
    (stepConfig) => stepConfig.path === pathname,
  );

  return matchedStep?.step ?? null;
};

export const getPathForStep = (
  step: RegisterStep,
  source: RegistrationFlowSource,
): AuthPath => {
  if (source === 'signin') {
    if (isLoginStep(step)) {
      return loginStepConfig[step].path;
    }

    return loginStepConfig.email.path;
  }

  return signupStepConfig[step].path;
};

const getSignupFirstStep = (): RegisterStep => {
  return registrationSteps[0].step;
};

const getSignupStepIndex = (step: RegisterStep): number => {
  return registrationSteps.findIndex((stepConfig) => stepConfig.step === step);
};

export const getPreviousRegisterStep = (step: RegisterStep): RegisterStep => {
  const currentStepIndex = getSignupStepIndex(step);
  if (currentStepIndex <= 0) {
    return getSignupFirstStep();
  }

  const previousStep = registrationSteps.at(currentStepIndex - 1);
  return previousStep ? previousStep.step : getSignupFirstStep();
};

export const getRegisterStepFields = <TStep extends RegisterStep>(
  step: TStep,
): (typeof registerStepConfig)[TStep]['fields'] => {
  return registerStepConfig[step].fields;
};

export const stepIncludesField = (
  step: RegisterStep,
  field: RegisterField,
): boolean => {
  return getRegisterStepDefinition(step).fields.includes(field);
};

export const isPersistedRegisterField = (
  field: RegisterField,
): field is RegisterPersistedField => {
  return persistedFieldSet.has(field);
};

export const getRegisterContinueEvent = (
  step: RegisterStep,
): RegisterContinueEvent | undefined => {
  return getRegisterStepDefinition(step).continueEvent;
};

export const getRegisterStepProgress = (
  step: RegisterStep,
): RegisterStepProgress | undefined => {
  const signupStepIndex = registrationSteps.findIndex(
    (stepConfig) => stepConfig.step === step,
  );
  const isProgressStep =
    signupStepIndex > 0 && signupStepIndex < registrationSteps.length - 1;

  if (!isProgressStep) {
    return undefined;
  }

  const profileTotalSteps = Math.max(registrationSteps.length - 2, 1);
  const profileStepIndex = signupStepIndex - 1;

  return {
    currentStep: profileStepIndex + 1,
    totalSteps: profileTotalSteps,
    showBack: profileStepIndex > 0,
  };
};

export const isProfileFlowStep = (
  step: RegisterStep,
): step is ProfileFlowStep => {
  return profileFlowStepSet.has(step);
};

export const getRegisterProgressRequirement = (
  step: RegisterStep,
): RegisterProgressRequirement | null => {
  const stepDefinition = getRegisterStepDefinition(step);
  if (!stepDefinition.participatesInProgress) {
    return null;
  }

  switch (stepDefinition.requirement) {
    case 'valid-email':
    case 'required-full-name':
    case 'required-instagram':
      return stepDefinition.requirement;
    case 'optional-valid-phone':
    case 'exact-confirmation-code':
      return null;
  }
};

export const getRegisterContinueRequirement = (
  step: RegisterStep,
): RegisterContinueRequirement => {
  return getRegisterStepDefinition(step).requirement;
};
